import { z } from 'zod';
import { Gender, Language, Platform, Theme, UserType } from '../enums.js';

export const createProfileBillingSchema = z
  .object({
    company: z.string().optional(),
    company_registration: z.string().optional(),
    address: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    state: z.string().optional(),
    zip_code: z.string().optional(),
  })
  .optional()
  .refine(
    (billing) => {
      if (!billing) return true;

      const hasAnyField = Object.values(billing).some((value) => value && value.trim().length > 0);

      if (!hasAnyField) return true;

      return Object.values(billing).every((value) => value && value.trim().length > 0);
    },
    {
      message: 'If any billing field is filled, all billing fields are required',
    },
  );

export const createProfileInstitutionSchema = z
  .object({
    display_name: z.string().min(1, 'Institution name is required'),
    avatar_url: z.string().optional(),
    avatar: z.string().optional(),
    address: z.string().min(1, 'Address is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zip_code: z.string().min(1, 'Zip code is required'),
    country: z.string().min(1, 'Country is required'),
    company_registration: z.string().min(1, 'Company registration is required'),
  })
  .optional();

export const createProfileSchema = z
  .object({
    avatar_url: z.string().optional(),
    avatar: z.string().optional(),
    first_name: z.string().min(1, 'First name is required'),
    last_name: z.string().min(1, 'Last name is required'),
    gender: z.enum(Gender),
    phone: z.string().optional(),
    type: z.enum(UserType).optional(),
    language: z.enum(Language).optional(),
    theme: z.enum(Theme).optional(),
    push_enabled: z.boolean().optional(),
    platform: z.enum(Platform).optional(),
    expo_token: z.string().optional(),
    billing: createProfileBillingSchema.optional(),
    institution: createProfileInstitutionSchema.optional(),
    health_data_consent: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.type === 'INSTITUTION' && !data.institution) {
        return false;
      }
      return true;
    },
    {
      message: 'Institution details are required when type is INSTITUTION',
      path: ['institution'],
    },
  );

export type CreateProfilePayload = z.infer<typeof createProfileSchema>;
export type CreateProfileInstitutionPayload = z.infer<typeof createProfileInstitutionSchema>;
